import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { usePortal } from "@/contexts/PortalContext";
import { getPortalForRole, type PortalId } from "@/lib/portal";
import { getSessionIndicator, setSessionIndicator } from "@/lib/session-indicator";
import { Loader2, RefreshCw } from "lucide-react";

type ProtectedRouteProps = {
  requiredRoles: string[];
  children: React.ReactNode;
};

// Clearly visible loading state — slate-50 bg so it's obviously not a blank page
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
    <div className="w-16 h-16 rounded-2xl bg-brand-forest/10 flex items-center justify-center shadow-sm">
      <Loader2 className="w-8 h-8 animate-spin text-brand-forest" />
    </div>
    <p className="text-sm font-semibold text-slate-500 tracking-wide">Loading your portal…</p>
  </div>
);

const SuspendedScreen = () => {
  const { profile } = useAuth();
  const { branding } = useBranding();
  const isDeactivated = profile?.status === "deactivated";
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🚫</span>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {isDeactivated ? "Account Deactivated" : "Account Suspended"}
        </h1>
        <p className="text-muted-foreground mb-6">
          {isDeactivated
            ? "Your account has been deactivated. If you wish to reactivate, please contact support at "
            : "Your account has been suspended. Please contact support at "}
          <a href={`mailto:${branding.supportEmail}`} className="text-brand-green-dk hover:underline">
            {branding.supportEmail}
          </a>{" "}
          for assistance.
        </p>
      </div>
    </div>
  );
};

const ProfileErrorScreen = () => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 p-6">
    <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
      <RefreshCw className="w-7 h-7 text-red-400" />
    </div>
    <div className="text-center max-w-xs">
      <p className="text-sm font-semibold text-slate-700 mb-1">Unable to load your account</p>
      <p className="text-xs text-slate-400 mb-4">There was a problem fetching your profile. Please refresh the page.</p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-2 text-xs font-semibold text-brand-forest hover:underline"
      >
        <RefreshCw className="w-3.5 h-3.5" /> Refresh
      </button>
    </div>
  </div>
);

const ProtectedRoute = ({ requiredRoles, children }: ProtectedRouteProps) => {
  const { user, profile, loading, profileError, mfaNeedsChallenge, signOut } = useAuth();
  const { isPortalMode, getPortalUrl, portal } = usePortal();

  // Reactive indicator state — re-read on focus / visibility change so other
  // tabs on this portal evict themselves the moment the user returns to them.
  // Cookies don't fire storage events, so a 1s poll is the safety net.
  const [indicatorPortal, setIndicatorPortal] = useState<PortalId | "signed_out" | null>(
    (isPortalMode && portal) ? getSessionIndicator() : null,
  );

  useEffect(() => {
    if (!isPortalMode || !portal) return;
    const recheck = () => setIndicatorPortal(getSessionIndicator());
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    const interval = setInterval(recheck, 1000);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
      clearInterval(interval);
    };
  }, [isPortalMode, portal]);

  // Mismatch when:
  //  (a) indicator is "signed_out" — someone explicitly logged out everywhere, OR
  //  (b) indicator points to a different portal — another portal took over.
  // We do NOT evict on null — that just means the cookie is unset/expired, which
  // is not a security signal and would cause false evictions.
  const portalMismatch = !!(isPortalMode && portal && user && (
    indicatorPortal === "signed_out" ||
    (indicatorPortal !== null && indicatorPortal !== "signed_out" && indicatorPortal !== portal)
  ));

  useEffect(() => {
    if (portalMismatch) signOut();
  }, [portalMismatch, signOut]);

  // If the indicator cookie is gone (expired) but we have an active session,
  // re-claim ownership. This prevents two portals from coexisting silently
  // after the 400-day cookie eventually expires. Whichever portal loads first
  // wins; the other sees the mismatch on the next 1-second poll and evicts.
  useEffect(() => {
    if (!isPortalMode || !portal || !user || indicatorPortal !== null) return;
    setSessionIndicator(portal as PortalId);
  }, [isPortalMode, portal, user, indicatorPortal]);

  if (loading) return <LoadingScreen />;
  if (portalMismatch) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (mfaNeedsChallenge) return <Navigate to="/mfa-challenge" replace state={{ returnTo: window.location.pathname }} />;
  // Profile fetch failed — show a recoverable error rather than a permanent blank screen
  if (profileError) return <ProfileErrorScreen />;
  if (!profile) return <LoadingScreen />;
  if (profile.status === "pending") return <Navigate to="/app/pending-approval" replace />;
  if (profile.status === "suspended" || profile.status === "deactivated") return <SuspendedScreen />;

  if (!requiredRoles.includes(profile.role)) {
    // On a portal subdomain: send the user to the login page of their correct portal
    // rather than the generic /app/403 page.
    if (isPortalMode) {
      const correctPortal = getPortalForRole(profile.role);
      if (correctPortal) {
        const correctLoginUrl = getPortalUrl(correctPortal, "/login");
        // Full cross-origin URL → use window.location, else use Navigate
        if (correctLoginUrl.startsWith("http")) {
          window.location.href = correctLoginUrl;
          return <LoadingScreen />;
        }
        return <Navigate to={correctLoginUrl} replace />;
      }
    }
    return <Navigate to="/app/403" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
